(() => {
  'use strict';

  if (window.__distopionCartRewards) {
    window.__distopionCartRewards.refresh();
    return;
  }

  if (!window.fetch || !window.Shopify?.routes?.root) return;

  const root = window.Shopify.routes.root;
  const rewardStates = new Map();
  let latestCart = null;
  let cartRequest = null;
  let readTimer = 0;
  let renderFrame = 0;
  let pendingRewardModal = null;
  let pendingRewardTimer = 0;

  const isCart = (cart) =>
    Array.isArray(cart?.items) && typeof cart?.currency === 'string';

  const isManagedGift = (item) =>
    String(item?.properties?._complimentary_gift || '') === 'true' ||
    String(item?.properties?._reward_choice || '') === 'true';

  const merchandiseSubtotal = (cart) => {
    if (!isCart(cart)) return 0;

    return cart.items.reduce((total, item) => {
      if (isManagedGift(item)) return total;

      return total + Math.max(
        0,
        Number(
          item?.original_line_price ??
          item?.final_line_price ??
          item?.line_price ??
          0
        )
      );
    }, 0);
  };

  const currencyRate = (shopCurrency, cartCurrency) => {
    if (!shopCurrency || !cartCurrency) return null;
    if (shopCurrency === cartCurrency) return 1;

    const activeCurrency = String(window.Shopify?.currency?.active || '').toUpperCase();
    const rate = Number(window.Shopify?.currency?.rate);

    return activeCurrency === cartCurrency && Number.isFinite(rate) && rate > 0
      ? rate
      : null;
  };

  const formatMoney = (minorUnits, currency) => {
    const amount = Math.max(0, Number(minorUnits || 0)) / 100;
    const hasDecimals = Math.round(amount) !== amount;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return amount.toFixed(hasDecimals ? 2 : 0) + ' ' + currency;
    }
  };

  const replaceRewardTokens = (template, replacements) =>
    Object.entries(replacements).reduce(
      (copy, [token, value]) => copy.replaceAll(token, value),
      String(template || '')
    );

  const glowReward = (card) => {
    card.classList.remove('is-just-unlocked');
    void card.offsetWidth;
    card.classList.add('is-just-unlocked');
    window.setTimeout(() => card.classList.remove('is-just-unlocked'), 1800);
  };

  const syncGroup = (group, cart) => {
    if (!isCart(cart)) return false;

    const shopCurrency = String(group.dataset.shopCurrency || '').toUpperCase();
    const cartCurrency = String(cart.currency || '').toUpperCase();
    const rate = currencyRate(shopCurrency, cartCurrency);
    if (!rate) return false;

    const currentTotal = merchandiseSubtotal(cart);
    const rewards = Array.from(group.querySelectorAll('[data-reward-card]'))
      .map((card) => ({
        card,
        id: String(card.dataset.rewardId || card.dataset.rewardTitle || ''),
        title: String(card.dataset.rewardTitle || 'reward'),
        threshold: Math.ceil(
          Math.max(0, Number(card.dataset.rewardThreshold || 0)) * rate
        ),
      }))
      .filter((reward) => reward.threshold > 0);

    rewards.forEach((reward) => {
      const unlocked = currentTotal >= reward.threshold;
      const wasUnlocked = rewardStates.get(reward.id);
      const remaining = Math.max(0, reward.threshold - currentTotal);
      const replacements = {
        __THRESHOLD__: formatMoney(reward.threshold, cartCurrency),
        __AMOUNT__: formatMoney(remaining, cartCurrency),
      };

      reward.card.querySelectorAll('[data-reward-dynamic-copy]').forEach((element) => {
        const template = element.dataset.rewardCopyTemplate || element.innerHTML;
        element.innerHTML = replaceRewardTokens(template, replacements);
      });

      reward.card.classList.toggle('is-unlocked', unlocked);
      reward.card.classList.toggle('is-locked', !unlocked);
      reward.card.setAttribute('aria-label', reward.title);

      const message = reward.card.querySelector('[data-reward-message]');
      if (message) {
        const template = String(
          unlocked
            ? reward.card.dataset.unlockedMessage || ''
            : reward.card.dataset.lockedMessage || '__THRESHOLD__'
        );
        message.textContent = replaceRewardTokens(template, replacements);
        message.hidden = template === '';
      }

      const action = reward.card.querySelector('[data-reward-action]');
      if (action && message?.parentElement && action.parentElement !== message.parentElement) {
        message.insertAdjacentElement('afterend', action);
      }
      if (action) action.hidden = !unlocked;

      if (
        unlocked &&
        wasUnlocked !== undefined &&
        wasUnlocked !== true &&
        group.dataset.rewardGlow !== 'false'
      ) {
        glowReward(reward.card);
      }

      rewardStates.set(reward.id, unlocked);
    });

    if (!rewards.length) return true;

    const sorted = [...rewards].sort((a, b) => a.threshold - b.threshold);
    const maximum = Math.max(...sorted.map((reward) => reward.threshold), 1);
    const next = sorted.find((reward) => currentTotal < reward.threshold);
    const percent = Math.min(100, Math.max(0, currentTotal / maximum * 100));

    group.classList.toggle('is-complete', !next);

    const overviewMessage = group.querySelector('[data-rewards-message]');
    if (overviewMessage) {
      const nextTemplate = String(
        group.dataset.nextRewardMessage || '__AMOUNT__'
      );
      const completeTemplate = String(
        group.dataset.allRewardsMessage || ''
      );

      overviewMessage.textContent = next
        ? replaceRewardTokens(nextTemplate, {
            __AMOUNT__: formatMoney(next.threshold - currentTotal, cartCurrency),
            __THRESHOLD__: formatMoney(next.threshold, cartCurrency),
            __REWARD__: next.title,
          })
        : completeTemplate;
    }

    const progress = group.querySelector('[data-rewards-progress]');
    if (progress) progress.setAttribute('aria-valuenow', String(Math.round(percent)));

    const fill = group.querySelector('[data-rewards-progress-fill]');
    if (fill) fill.style.width = percent.toFixed(3) + '%';

    return true;
  };

  const syncAll = (cart = latestCart) => {
    if (!isCart(cart)) return false;

    let synced = false;
    document.querySelectorAll('[data-cart-rewards]').forEach((group) => {
      synced = syncGroup(group, cart) || synced;
    });
    return synced;
  };

  const scheduleRender = (cart) => {
    if (isCart(cart)) latestCart = cart;
    if (renderFrame) return;

    renderFrame = requestAnimationFrame(() => {
      renderFrame = 0;
      syncAll(latestCart);
    });
  };

  const fetchCart = () => {
    if (cartRequest) return cartRequest;

    cartRequest = fetch(root + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart request failed (' + response.status + ')');
        return response.json();
      })
      .then((cart) => {
        latestCart = cart;
        scheduleRender(cart);
        return cart;
      })
      .catch((error) => {
        console.error('[Distopion cart rewards]', error);
        return null;
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  };

  const queueRead = (delay = 100) => {
    window.clearTimeout(readTimer);
    readTimer = window.setTimeout(fetchCart, delay);
  };

  const closePendingRewardModal = () => {
    if (!pendingRewardModal) return;

    const modal = pendingRewardModal;
    pendingRewardModal = null;
    window.clearTimeout(pendingRewardTimer);
    pendingRewardTimer = 0;

    if (typeof modal.hide === 'function') {
      modal.hide();
      return;
    }

    if (typeof modal.close === 'function') {
      modal.close();
      return;
    }

    modal.querySelector('[is="close-button"], [data-action="close"], button[aria-label*="Close" i]')?.click();
  };

  const handleCartEvent = (event) => {
    const cart = event.detail?.cart;
    if (isCart(cart)) scheduleRender(cart);
    if (event.type === 'cart:change') closePendingRewardModal();
    queueRead();
  };

  const chooseRewardProduct = (trigger) => {
    const modal = trigger.closest('.cart-reward-modal');
    const chosenProduct = trigger.closest('[data-reward-product]');
    if (!modal || !chosenProduct) return;

    modal.querySelectorAll('[data-reward-product]').forEach((product) => {
      const selected = product === chosenProduct;
      const productTrigger = product.querySelector('[data-reward-product-select]');
      const options = product.querySelector('[data-reward-product-options]');
      const submit = product.querySelector('[data-reward-product-submit]');

      product.classList.toggle('is-selected', selected);
      if (productTrigger) productTrigger.setAttribute('aria-expanded', String(selected));
      if (options) options.hidden = !selected;

      if (!selected && submit) submit.hidden = true;
    });

    const options = chosenProduct.querySelector('[data-reward-product-options]');
    const selector = chosenProduct.querySelector('[data-reward-variant-select]');
    const submit = chosenProduct.querySelector('[data-reward-product-submit]');

    if (options) options.hidden = false;
    if (submit) submit.hidden = selector ? !selector.value : false;

    window.requestAnimationFrame(() => {
      chosenProduct.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    });
  };

  const handleRewardClick = (event) => {
    const trigger = event.target.closest('[data-reward-product-select]');
    if (trigger) chooseRewardProduct(trigger);
  };

  const handleRewardOptionChange = (event) => {
    const selector = event.target.closest('[data-reward-variant-select]');
    if (!selector) return;

    const product = selector.closest('[data-reward-product]');
    const submit = product?.querySelector('[data-reward-product-submit]');
    if (submit) submit.hidden = !selector.value;
  };

  const handleRewardSubmit = (event) => {
    const form = event.target.closest('.cart-reward-modal form');
    if (!form) return;

    const modal = form.closest('x-modal');
    if (!modal) return;

    pendingRewardModal = modal;
    window.clearTimeout(pendingRewardTimer);
    pendingRewardTimer = window.setTimeout(() => {
      pendingRewardModal = null;
      pendingRewardTimer = 0;
    }, 15000);
  };

  const refresh = () => {
    if (isCart(latestCart)) scheduleRender(latestCart);
    queueRead(0);
  };

  const start = () => {
    document.addEventListener('cart:change', handleCartEvent, { passive: true });
    document.addEventListener('cart:refresh', handleCartEvent, { passive: true });
    document.addEventListener('shopify:section:load', refresh, { passive: true });
    document.addEventListener('click', handleRewardClick);
    document.addEventListener('change', handleRewardOptionChange);
    document.addEventListener('submit', handleRewardSubmit);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refresh();
    });
    refresh();
  };

  window.__distopionCartRewards = { refresh, syncAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
